import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, ChevronRight, Search, Filter } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AccountOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/account/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-indigo-100 text-indigo-800',
      packed: 'bg-purple-100 text-purple-800',
      shipped: 'bg-cyan-100 text-cyan-800',
      out_for_delivery: 'bg-orange-100 text-orange-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      returned: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredOrders = orders.filter(order => {
    if (!statusFilter) return true;
    return order.order_status === statusFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="account-orders">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-heading text-2xl font-light text-charcoal">My Orders</h1>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 px-4 border border-charcoal/10 focus:border-gold outline-none bg-white text-sm"
        >
          <option value="">All Orders</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {filteredOrders.length > 0 ? (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <Link
              key={order.id}
              to={`/account/orders/${order.id}`}
              className="block bg-white border border-charcoal/10 hover:border-gold transition-colors"
            >
              {/* Order Header */}
              <div className="p-4 border-b border-charcoal/10 flex items-center justify-between bg-beige/30">
                <div className="flex items-center gap-6">
                  <div>
                    <p className="text-xs text-charcoal-light">ORDER PLACED</p>
                    <p className="text-sm text-charcoal">{formatDate(order.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-charcoal-light">TOTAL</p>
                    <p className="text-sm font-medium text-charcoal">{formatCurrency(order.total_amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-charcoal-light">SHIP TO</p>
                    <p className="text-sm text-charcoal">{order.customer_name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-charcoal-light">ORDER #{order.order_number}</p>
                  <span className={`inline-block mt-1 px-2 py-0.5 text-xs ${getStatusColor(order.order_status)}`}>
                    {order.order_status?.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Order Items */}
              <div className="p-4">
                <div className="flex items-center gap-4">
                  {order.items?.slice(0, 3).map((item, idx) => (
                    <div key={idx} className="w-16 h-16 bg-beige flex-shrink-0">
                      {item.image ? (
                        <img src={item.image} alt={item.product_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-6 h-6 text-charcoal-light" />
                        </div>
                      )}
                    </div>
                  ))}
                  {order.items?.length > 3 && (
                    <div className="w-16 h-16 bg-beige flex items-center justify-center flex-shrink-0">
                      <span className="text-sm text-charcoal-light">+{order.items.length - 3}</span>
                    </div>
                  )}
                  
                  <div className="flex-1 ml-4">
                    <p className="text-sm text-charcoal">
                      {order.items?.[0]?.product_name}
                      {order.items?.length > 1 && ` and ${order.items.length - 1} more item${order.items.length > 2 ? 's' : ''}`}
                    </p>
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-charcoal-light" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-charcoal/10 p-12 text-center">
          <Package className="w-16 h-16 mx-auto text-charcoal-light mb-4" />
          <h3 className="font-heading text-xl font-light text-charcoal mb-2">No orders yet</h3>
          <p className="text-charcoal-light mb-4">Start shopping to see your orders here</p>
          <Link
            to="/products"
            className="inline-block bg-gold text-white px-6 py-2 text-sm font-medium hover:bg-gold-light transition-colors"
          >
            Browse Products
          </Link>
        </div>
      )}
    </div>
  );
};

export default AccountOrders;
